import convert from 'color-convert';
import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from 'geojson';
import { MapRef } from 'react-map-gl';
import {
  CubeDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
} from '../../services/data/impl/cube/cubeSlice';
import { Block } from '../../services/entities/impl/blfApi/schemas/block';
import { GPSCoordinates } from '../commonUI/maps/types';
import { roundFractional } from '../ranches/ranchUtils';
import {
  DEFAULT_HEATMAP_COLORS,
  blueberryColorConfigs,
} from '../scanDataApi/hooks/useBlueberryDistributionByClass';
import {
  BlueberryData,
  BlueberryDataPerClass,
} from '../scanDataApi/hooks/useBlueberryQuery';
import { MapPointsQueryResult } from '../scanDataApi/hooks/useMapPointsScanData';
import {
  ScanDataEnum,
  ScanDataMeasureEnum,
} from '../scanDataApi/scanDataDefinitions';
import { EstimatedCountSumQueryResult } from './features/measurePopup/MapMeasureDialogContent';
import {
  fadeInDuration,
  fadeOutDuration,
  fadeOutExtraCircleRadius,
  pointRadiusAtStartZoomLevel,
} from './layers/PointDataLayers';
import { PointProperties } from './Mapbox';
import { LayerEnum } from './mapSlice';
import { combineFeatureCollections } from './utils/combineFeatureCollections';
import { EMPTY_FEATURE_COLLECTION } from './utils/constants';

/**
 * Converts a string in the format 'SRID=4326;POINT(35.8678957500786 -119.207778025748)'
 * into an object with a latitude and longitude. The values will be rounded to 8 digits
 * of precision which gives 1.11mm accuracy (improves performance and reduces memory usage of mapbox, see: https://docs.mapbox.com/help/troubleshooting/working-with-large-geojson-data/ ).
 * @param str - The input string to convert.
 * @returns An object with properties 'latitude' and 'longitude' or null if any errors occur.
 */
export function convertStringToLatLng(str: string): GPSCoordinates | null {
  try {
    // Split the string by ';'
    const parts = str.split(';');
    // Check if the first part is 'SRID=4326'
    if (parts[0] !== 'SRID=4326') {
      return null;
    }
    // Split the second part by '(' and ')'
    const coords = parts[1].split(/[()]/);
    // Check if the first element is 'POINT'
    if (coords[0] !== 'POINT') {
      return null;
    }
    // Split the coordinates by ' '
    const latLng = coords[1].split(' ');
    // Parse the coordinates as numbers and round to 9 digits of precision
    const latitude = Number(parseFloat(latLng[0]).toFixed(8));
    const longitude = Number(parseFloat(latLng[1]).toFixed(8));
    // Return the latitude and longitude as an object
    return {
      latitude,
      longitude,
    };
  } catch {
    // Return null if any errors occur
    return null;
  }
}

export interface CubeRawData {
  [CubeDimensionEnum.pointLocation]: string;
  [CubeDimensionEnum.detectionFactPointId]: string;
  [CubeDimensionEnum.rowsideRowNum]: number;
  [CubeDimensionEnum.detectionFactRowsideId]: string;
  [CubeDimensionEnum.rowsideViewDirection]: string;
  [CubeMeasureEnum.detectionFactCountPerMeter]?: number;
}

// The required fields for a cube data point
const requiredFields = [
  CubeDimensionEnum.pointLocation,
  CubeDimensionEnum.detectionFactPointId,
  CubeDimensionEnum.rowsideRowNum,
  CubeDimensionEnum.rowsideViewDirection,
];

/**
 * Check if the cube data has all of the required fields
 * @param requiredFields A string array of required fields
 * @param point The point data to check
 * @returns Boolean if the cube data has all of the required fields
 */
export function hasRequiredCubeFields(point: CubeRawData | undefined): boolean {
  if (!point) return false;
  return requiredFields.every((field) => field in point);
}

/**
 * Determines the color of a data point based on its deviation from the average.
 * The color reflects the standard deviation of the data point's value from the block's mean.
 *
 * @param blockStdDev The standard deviation of the block
 * @param meanPerBlock The mean of the block
 * @param currValue The current value being evaluated
 * @returns The color associated with the deviation of the current value
 */
export const getColorByStdDev = (
  blockStdDev: number,
  meanPerBlock: number,
  currValue: number,
  customColors?: [string, string, string, string, string, string]
) => {
  const negativeSegmentsFromLeft = Math.min(
    Math.floor(meanPerBlock / blockStdDev),
    2
  );

  const colors = customColors || DEFAULT_HEATMAP_COLORS;

  if (currValue > meanPerBlock - blockStdDev * -2) return colors[0];
  if (currValue > meanPerBlock - blockStdDev * -1) return colors[1];
  if (currValue > meanPerBlock - blockStdDev * 0) return colors[2];

  const negativeColors = [colors[3], colors[4], colors[5]];

  for (let i = 1; i <= negativeSegmentsFromLeft; i++) {
    if (currValue > meanPerBlock - blockStdDev * i)
      return negativeColors[i - 1];
  }

  return negativeColors[negativeSegmentsFromLeft];
};

/**
 * Checks if the given item is a key of the BlueberryDataPerClass interface.
 *
 * @param {string} item - The item to check.
 * @returns {boolean} - Returns true if the item is a key of BlueberryDataPerClass, false otherwise.
 */
function isKeyOfBlueberryDataPerClass(
  item: string
): item is keyof BlueberryDataPerClass {
  return Object.values(ScanDataEnum).includes(item as ScanDataEnum);
}

export const DEFAULT_GPS_POINT_COLOR = '#000';

export const createPointFeaturesFromBlueberriesData = (
  blueberriesData: BlueberryData[] | null,
  className: FeatureEnum | null,
  mean: number | null,
  stdDev: number | null,
  measure: string
): Feature<Geometry>[] | null => {
  // Check if scanData is empty or null
  if (!blueberriesData || blueberriesData.length < 1) return null;

  // Check if measure is in the blueberry data
  let validatedMeasure: keyof BlueberryDataPerClass;
  if (isKeyOfBlueberryDataPerClass(measure as unknown as string)) {
    validatedMeasure = measure as unknown as keyof BlueberryDataPerClass;
  } else {
    return null;
  }

  // Initialize array to hold point features
  const features: Feature<Geometry>[] = [];

  blueberriesData?.forEach((point) => {
    // Convert point location to latitude and longitude
    const latLng = convertStringToLatLng(point.point_location);

    if (latLng) {
      let color = DEFAULT_GPS_POINT_COLOR;

      if (!className) {
        // total detections - all flowers and berries
        const blueberriesValue =
          (point?.blueberry_Blue?.[validatedMeasure] || 0) +
          (point?.blueberry_Green?.[validatedMeasure] || 0) +
          (point?.blueberry_Purple?.[validatedMeasure] || 0) +
          (point?.blueberry_Yellow?.[validatedMeasure] || 0);

        const flowersValue =
          (point?.flower_closed?.[validatedMeasure] || 0) +
            (point?.flower_open?.[validatedMeasure] || 0) +
            (point?.flower_petalfall?.[validatedMeasure] || 0) ||
          point?.flower?.[validatedMeasure] ||
          0;

        const value = blueberriesValue + flowersValue;

        if (mean && stdDev) {
          color = getColorByStdDev(stdDev, mean, value);
        }
      } else if (className === FeatureEnum.blueberry) {
        // all blueberries
        const value =
          (point?.blueberry_Blue?.[validatedMeasure] || 0) +
          (point?.blueberry_Green?.[validatedMeasure] || 0) +
          (point?.blueberry_Purple?.[validatedMeasure] || 0) +
          (point?.blueberry_Yellow?.[validatedMeasure] || 0);

        if (mean && stdDev) {
          color = getColorByStdDev(stdDev, mean, value);
        }
      } else if (className === FeatureEnum.flower) {
        const value =
          (point?.flower_closed?.[validatedMeasure] || 0) +
            (point?.flower_open?.[validatedMeasure] || 0) +
            (point?.flower_petalfall?.[validatedMeasure] || 0) ||
          point?.flower?.[validatedMeasure] ||
          0;

        if (mean && stdDev) {
          color = getColorByStdDev(stdDev, mean, value);
        }
      } else {
        const value =
          point?.[
            className as
              | FeatureEnum.blueberry_Blue
              | FeatureEnum.blueberry_Purple
              | FeatureEnum.blueberry_Yellow
              | FeatureEnum.blueberry_Green
              | FeatureEnum.flower_closed
              | FeatureEnum.flower_open
              | FeatureEnum.flower_petalfall
          ]?.[validatedMeasure] || 0;

        if (mean && stdDev) {
          color = getColorByStdDev(
            stdDev,
            mean,
            value,
            Object.hasOwn(blueberryColorConfigs, className)
              ? blueberryColorConfigs[className]
              : DEFAULT_HEATMAP_COLORS
          );
        }
      }
      const newProperties: PointProperties = {
        point_id: point.point_id,
        point_location: point.point_location,
        rowside_id: point.rowside_id,
        rowside_num: point.rowside_num,
        view_direction: point.view_direction,
        color, // Add the calculated color property
        hueValue: undefined,
        flowers: point?.[FeatureEnum.flower]?.[validatedMeasure] || 0,
      };

      // Create new feature and add it to the features array
      const newFeature: Feature<Geometry> = {
        type: 'Feature',
        properties: {
          ...newProperties,
        },
        geometry: {
          type: 'Point',
          coordinates: [latLng.longitude, latLng.latitude],
        },
      };
      features.push(newFeature);
    }
  });

  // Return either the features array or null if no features were created
  return features.length > 0 ? features : null;
};

/**
 * Creates GeoJSON point features from an array of ScanData.
 * The features are colored based on the standard deviation and mean block count.
 * If the ScanData is null or empty, the function returns null.
 *
 * @param {ScanData[] | null | undefined} scanData - An array of scanData objects.
 * @param {ScanDataMeasureEnum} measure - The current scan data measure dropdown selection.
 * @param {ResultSet | null} blockStdDevCount - A ResultSet object containing the standard deviation and mean counts per block.
 * @param {boolean} useCustomSaturation - Whether or not to use a custom saturation value for HSV.
 * @param {boolean} useCustomValue - Whether or not to use a custom value (brightness) for HSV.
 * @param {number} [newSaturation] - Optional: the new saturation value for HSV.
 * @param {number} [newValue] - Optional: the new value (brightness) for HSV.
 * @returns {Feature<Geometry>[] | null} An array of GeoJSON point features or null.
 */
export const createPointFeaturesFromScanData = (
  scanData: MapPointsQueryResult[] | null | undefined,
  measure: ScanDataMeasureEnum,
  stdDevPerBlock: number | null,
  meanPerBlock: number | null,
  useCustomSaturation: boolean,
  useCustomValue: boolean,
  newSaturation?: number,
  newValue?: number
): Feature<Geometry>[] | null => {
  // Check if scanData is empty or null
  if (!scanData || scanData.length < 1) return null;

  // Initialize array to hold point features
  const features: Feature<Geometry>[] = [];

  scanData?.forEach((point) => {
    // Convert point location to latitude and longitude
    const latLng = convertStringToLatLng(point.point_location);

    if (latLng) {
      let color = DEFAULT_GPS_POINT_COLOR;

      if (
        [
          ScanDataMeasureEnum.count_per_meter,
          ScanDataMeasureEnum.detection_length,
          ScanDataMeasureEnum.detection_area,
          ScanDataMeasureEnum.color_hsv,
        ].includes(measure)
      ) {
        // cast ScanDataMeasureEnum to MapPointsQueryResult
        const scanDataMeasure =
          measure as unknown as keyof MapPointsQueryResult;
        // Calculate color based on standard deviation and mean block count
        if (measure === ScanDataMeasureEnum.color_hsv) {
          // If color_hsv exists, use it as the color
          if (point.h != null && point.s != null && point.v != null) {
            const rgbString = HSVToRGB(
              [point.h, point.s, point.v],
              useCustomSaturation,
              useCustomValue,
              newSaturation,
              newValue
            );
            if (rgbString) {
              color = rgbString;
            }
          }
        } else if (
          scanDataMeasure.toString() in point &&
          typeof point[scanDataMeasure] === 'number' &&
          point[scanDataMeasure] !== null
        ) {
          if (stdDevPerBlock && meanPerBlock) {
            color = getColorByStdDev(
              stdDevPerBlock,
              meanPerBlock,
              point[scanDataMeasure] as number
            );
          }
        }
      }

      const newProperties: PointProperties = {
        point_id: point.point_id,
        point_location: point.point_location,
        rowside_id: point.rowside_id,
        rowside_num: point.rowside_num,
        view_direction: point.view_direction,
        color, // Add the calculated color property
        hueValue: point.h,
      };

      // Create new feature and add it to the features array
      const newFeature: Feature<Geometry> = {
        type: 'Feature',
        properties: {
          ...newProperties,
        },
        geometry: {
          type: 'Point',
          coordinates: [latLng.longitude, latLng.latitude],
        },
      };
      features.push(newFeature);
    }
  });

  // Return either the features array or null if no features were created
  return features.length > 0 ? features : null;
};

/**
 * Finds the maximum value of a specified property inside a GeoJSON FeatureCollection
 * @param featureCollection  a GeoJSON FeatureCollection
 * @param propertyName the property name to get the max value from
 * @returns the maximum value of the specified property or null if an error occurs
 */
function findMaxPropertyValue(
  featureCollection: FeatureCollection,
  propertyName: string
): number | null {
  if (!featureCollection || !featureCollection.features) {
    return null;
  }
  let maxValue = Number.MIN_VALUE;
  featureCollection.features.forEach((feature) => {
    if (!feature.properties || !feature.properties[propertyName]) {
      return null;
    }
    if (typeof feature.properties[propertyName] !== 'number') {
      return null;
    }
    if (feature.properties[propertyName] > maxValue) {
      maxValue = feature.properties[propertyName];
    }
  });
  return maxValue;
}

const getHeatmapColorsDistributionByRawData = (
  stdDev: number,
  mean: number
) => {
  const colors = [
    '#097969',
    '#228B22',
    '#50C878',
    '#90EE90',
    '#E4D00A',
    '#FF5733',
  ];

  const values = [
    mean - -2 * stdDev,
    mean - -1 * stdDev,
    mean - 0 * stdDev,
    mean - 1 * stdDev,
    mean - 2 * stdDev,
  ].filter((value) => value > 0);

  const ranges: [number, number, string][] = [];

  values.forEach((value, i) => {
    ranges.push([value, i !== 0 ? values[i - 1] : Number.MAX_VALUE, colors[i]]);
  });

  ranges.push([0, values[values.length - 1], colors[values.length]]);
  return ranges.reverse();
};

/**
 * Converts blocks with geoOrientation data into a consolidated GeoJSON feature collection.
 * This function iterates through each block, checking for the presence of geoOrientation data.
 * For blocks with geoOrientation, it modifies each orientation feature to include its ID within
 * the properties object. This is necessary for later filtering and styling based on feature IDs
 * (e.g., to differentiate between xNumberOrientation and yRowOrientation lines).
 * If no blocks contain geoOrientation data, an empty feature collection is returned.
 *
 * @param {Block[]} blocks The array of blocks to process.
 * @returns {FeatureCollection<Geometry, GeoJsonProperties>} A GeoJSON feature collection of all orientations.
 */
export const convertOrientationsToFeatureCollection = (blocks: Block[]) => {
  const blockFeatureCollections: FeatureCollection<
    Geometry,
    GeoJsonProperties
  >[] = [];

  blocks.forEach((block) => {
    if (block.geoOrientation) {
      // place the id in the properties of the feature so the layer can use it to color the lines
      const newGeoOrientationFeatures = block.geoOrientation.features.map(
        (feature) => ({
          ...feature,
          properties: {
            id: feature.id,
          },
        })
      );

      blockFeatureCollections.push({
        ...block.geoOrientation,
        features: newGeoOrientationFeatures,
      });
    }
  });

  if (blockFeatureCollections.length === 0) {
    return EMPTY_FEATURE_COLLECTION;
  }

  return combineFeatureCollections(blockFeatureCollections);
};

/**
 * Converts an HSV string from the format "[0.6226851851851852,0.2823529411764706,1.0]" to "rgb(207, 255, 195)".
 * Modifies saturation and value if provided.
 * Returns null if the input format is incorrect or the values are out of range.
 * @param {string} hsvString - The HSV string in the format "[0.6226851851851852,0.2823529411764706,1.0]".
 * @param {boolean} useCustomSaturation - Whether to use a custom saturation value.
 * @param {boolean} useCustomValue - Whether to use a custom HSV value (brightness).
 * @param {number} [newSaturation] - Optional: the new saturation value for HSV.
 * @param {number} [newValue] - Optional: the new value (brightness) for HSV.
 * @return {string | null} - The standard RGB format string or null if an error occurred.
 */
export const HSVToRGB = (
  hsvArr: [number, number, number],
  useCustomSaturation: boolean,
  useCustomValue: boolean,
  newSaturation?: number,
  newValue?: number
): string | null => {
  try {
    // Modify saturation and value if required
    const modifiedHSV: [number, number, number] = [
      hsvArr[0],
      useCustomSaturation && newSaturation !== undefined
        ? newSaturation
        : hsvArr[1],
      useCustomValue && newValue !== undefined ? newValue : hsvArr[2],
    ];

    // Convert to RGB
    const newRGB = convert.hsv.rgb(modifiedHSV);

    return `rgb(${newRGB.join(', ')})`;
  } catch {
    return null;
  }
};

// Define a dummy object of PointProperties to extract keys
const dummyPointProperties: PointProperties = {
  point_id: '',
  point_location: '',
  rowside_id: '',
  rowside_num: 0,
  view_direction: '',
  color: '',
  hueValue: undefined,
};

const pointPropertyKeys = Object.keys(
  dummyPointProperties
) as (keyof PointProperties)[];

/**
 * Check if the given object conforms to the PointProperties interface.
 * This function allows certain keys to be optional or undefined, as specified within the function itself.
 *
 * @param obj - The object to check.
 * @return true if the object has all the required fields of PointProperties (except those marked as optional), false otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isPointProperties = (obj: any): obj is PointProperties => {
  // List the optional keys that are not required
  const optionalKeys: (keyof PointProperties)[] = ['hueValue'];

  // Check each key to ensure they exist in obj, or if they are optional, allow them to be missing or undefined
  return pointPropertyKeys.every((key) =>
    optionalKeys.includes(key)
      ? key in obj || obj[key] === undefined
      : key in obj
  );
};

/**
 * Ensures that the layer with the given name exists within the provided map reference.
 * @param mapRef {MapRef | null} - Reference to the map object
 * @param layerName {string} - Name of the layer to check
 * @returns {string | undefined} - The name of the layer if it exists, otherwise undefined
 */
export function ensureLayerExists(
  mapRef: MapRef | null,
  layerName: string | undefined
): string | undefined {
  if (!mapRef || !layerName) return undefined;
  return mapRef && layerName && mapRef.getLayer(layerName)
    ? layerName
    : undefined;
}

/**
 * Animate a point highlight on the map by applying fade-in and fade-out transitions.
 * @param currPointId - Current point ID, used to verify if the animation should be executed.
 * @param mapRef - Reference to the map object.
 * @returns A cleanup function to clear the timeout if necessary.
 */
export const animatePointHighlight = (currPointId: string, mapRef: MapRef) => {
  if (currPointId && mapRef) {
    try {
      const map = mapRef.getMap();
      map.setPaintProperty(
        `${LayerEnum.heatmapFadeOutHighlight}`,
        'circle-radius',
        pointRadiusAtStartZoomLevel + fadeOutExtraCircleRadius
      );

      map.setPaintProperty(
        `${LayerEnum.heatmapFadeOutHighlight}`,
        'circle-radius-transition',
        { duration: 0 }
      );

      map.setPaintProperty(
        `${LayerEnum.heatmapFadeOutHighlight}`,
        'circle-stroke-opacity-transition',
        { duration: fadeInDuration }
      );

      map.setPaintProperty(
        `${LayerEnum.heatmapFadeOutHighlight}`,
        'circle-stroke-opacity',
        1
      );

      // Trigger fade-out animation by setting opacity back to 0 after a short delay
      const timeoutId = setTimeout(() => {
        map.setPaintProperty(
          `${LayerEnum.heatmapFadeOutHighlight}`,
          'circle-radius',
          pointRadiusAtStartZoomLevel
        );

        map.setPaintProperty(
          `${LayerEnum.heatmapFadeOutHighlight}`,
          'circle-radius-transition',
          { duration: fadeOutDuration }
        );

        map.setPaintProperty(
          `${LayerEnum.heatmapFadeOutHighlight}`,
          'circle-stroke-opacity-transition',
          { duration: fadeOutDuration }
        );

        map.setPaintProperty(
          `${LayerEnum.heatmapFadeOutHighlight}`,
          'circle-stroke-opacity',
          0
        );
      }, 10); // 10ms delay to ensure the opacity 1 is rendered first

      // Clean up the timeout if necessary
      return () => {
        clearTimeout(timeoutId);
      };
    } catch (error) {
      console.error('Error animating point highlight', error);
    }
  }
};

/**
 * Sort Timestamps as Strings Function
 *
 * @param timestamps - An array of timestamps in custom string format.
 *
 * This function sorts an array of timestamps that are formatted as custom strings.
 * It leverages the fact that the custom timestamp strings are zero-padded and fixed size.
 *
 * @returns - An array of sorted timestamps in custom string format.
 */
export function sortTimestampsAsString(timestamps: string[]): string[] {
  return timestamps.sort((a, b) => (a > b ? 1 : a < b ? -1 : 0));
}

interface NameDict {
  [key: string]: string;
}

interface CopyPasteData {
  label: string;
  data: string;
}

/**
 * Generates a TSV (Tab-Separated Values) string from an array of CopyPasteData objects.
 * The first row consists of labels separated by tabs, and the second row consists of data points.
 *
 * @param items - Array of CopyPasteData objects containing labels and data
 * @returns A string formatted as TSV with CR and LF for row separators
 */
export const generateTSVFromArray = (items: CopyPasteData[]): string => {
  const tab = String.fromCharCode(9);
  const crlf = String.fromCharCode(13) + String.fromCharCode(10);

  // Extract labels and data points from items
  const labels = items.map((item) => item.label).join(tab);
  const dataPoints = items.map((item) => item.data).join(tab);

  // Combine labels and data points with CRLF
  return `${labels}${crlf}${dataPoints}`;
};

/**
 * Transforms an array of EstimatedCountSumQueryResult objects into an array of CopyPasteData.
 * Sorts data based on a predefined dictionary and returns it in CopyPasteData format.
 *
 * @param data - Array of EstimatedCountSumQueryResult objects
 * @returns An array of CopyPasteData objects with sorted labels and corresponding data
 */
export const transformToCopyPasteData = (
  data: EstimatedCountSumQueryResult[]
): CopyPasteData[] => {
  const nameDict: NameDict = {
    [FeatureEnum.flower_closed]: 'Closed',
    [FeatureEnum.flower_open]: 'Open',
    [FeatureEnum.flower_petalfall]: 'Petalfall',
    [FeatureEnum.flower]: 'Total Flowers',
    [FeatureEnum.blueberry_Green]: 'Green',
    [FeatureEnum.blueberry_Yellow]: 'Pink',
    [FeatureEnum.blueberry_Purple]: 'Purple',
    [FeatureEnum.blueberry_Blue]: 'Blue',
    [FeatureEnum.blueberry]: 'Total Berries',
  };

  // Sort the data array based on the order in nameDict
  data.sort((a, b) => {
    const nameA = a.class_name ? nameDict[a.class_name] : '';
    const nameB = b.class_name ? nameDict[b.class_name] : '';
    return (
      Object.values(nameDict).indexOf(nameA) -
      Object.values(nameDict).indexOf(nameB)
    );
  });

  // Map data to CopyPasteData format
  return data.map((row) => ({
    label: row.class_name ? nameDict[row.class_name] : '',
    data: roundFractional(row.totalCount, 0).toString(),
  }));
};

export { findMaxPropertyValue, getHeatmapColorsDistributionByRawData };
